<script setup lang="ts">
import InputError from '@/components/InputError.vue';
import InputLabel from '@/components/InputLabel.vue';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useRoute, useRouter } from 'vue-router';
import { nextTick, onMounted, ref } from 'vue';
import { useResetPassword } from '@/composables/api/useAuthApi';
import { AxiosError } from 'axios';

interface ResetPasswordErrors {
  email?: string[];
  password?: string[];
  password_confirmation?: string[];
  token?: string[];
  [key: string]: string[] | undefined;
}

const route = useRoute();
const router = useRouter();
const { mutate: resetPassword, isPending: isResetting } = useResetPassword();

const invalidToken = route.query.invalid_token;
const errors = ref<ResetPasswordErrors>({});
// The reset link carries the email address. A query parameter can repeat, so Vue Router may
// hand us an array. We use the first string and ignore the rest.
function firstQueryString(queryValue: unknown): string {
  const firstValue = Array.isArray(queryValue) ? queryValue[0] : queryValue;
  return typeof firstValue === 'string' ? firstValue.trim() : '';
}

const linkEmail = firstQueryString(route.query.email);
const hasLinkEmail = linkEmail !== '';
const email = ref(linkEmail);
const password = ref('');
const passwordConfirmation = ref('');

// The autofocus attribute only works on a full page load. It does nothing when the router
// swaps this page in, so we move the cursor ourselves. When the link already gave us the
// email, the cursor goes to the new password. Otherwise it goes to the email field.
onMounted(async () => {
  await nextTick();
  const fieldToFocus = document.getElementById(hasLinkEmail ? 'password' : 'email');
  fieldToFocus?.focus();
});

function resetForm() {
  password.value = '';
  passwordConfirmation.value = '';
  errors.value = {};
}

const handleSubmit = async (e: Event) => {
  e.preventDefault();
  
  resetPassword(
    {
      token: route.query.token as string,
      email: email.value,
      password: password.value,
      password_confirmation: passwordConfirmation.value,
    },
    {
      onSuccess: () => {
        resetForm();
        router.push({ name: 'login', query: { password_reset: 'true' } });
      },
      onError: (error: unknown) => {
        errors.value = {};
        
        if (error instanceof AxiosError && error.response?.status === 422) {
          const errorData = error.response.data;
          if (errorData.errors) {
            errors.value = errorData.errors;
          }
        }
      }
    }
  );
};
</script>

<template>
  <Card class="px-6 py-4">
    <form @submit="handleSubmit">
      <CardContent>
        <div class="space-y-2">
          <InputLabel for="email" value="Email" />

          <Input
            id="email"
            type="email"
            class="block w-full"
            required
            autocomplete="username"
            autocapitalize="none"
            spellcheck="false"
            name="email"
            :readonly="hasLinkEmail"
            :modelValue="email"
            @update:modelValue="(value: string | number) => email = String(value)"
            :disabled="isResetting"
            :class="{ 'border-red-500': errors['email'] }"
          />

          <InputError
            v-if="errors['email']"
            class="mt-1"
            :message="errors['email'][0]"
          />
          <InputError
            v-if="invalidToken"
            class="mt-1"
            :message="'This password reset token is invalid.'"
          />
        </div>

        <div class="mt-4 space-y-2">
          <InputLabel for="password" value="Password" />

          <Input
            id="password"
            type="password"
            class="block w-full"
            required
            autocomplete="new-password"
            name="password"
            :modelValue="password"
            @update:modelValue="(value: string | number) => password = String(value)"
            :disabled="isResetting"
            :class="{ 'border-red-500': errors['password'] }"
          />

          <InputError
            v-if="errors['password']"
            class="mt-1"
            :message="errors['password'][0]"
          />
        </div>

        <div class="mt-4 space-y-2">
          <InputLabel for="password_confirmation" value="Confirm Password" />

          <Input
            id="password_confirmation"
            type="password"
            class="block w-full"
            required
            autocomplete="new-password"
            name="password_confirmation"
            :modelValue="passwordConfirmation"
            @update:modelValue="(value: string | number) => passwordConfirmation = String(value)"
            :disabled="isResetting"
            :class="{ 'border-red-500': errors['password_confirmation'] }"
          />

          <InputError
            v-if="errors['password_confirmation']"
            class="mt-1"
            :message="errors['password_confirmation'][0]"
          />
        </div>
      </CardContent>
      <CardFooter>
        <div class="flex items-center justify-end mt-4">
          <Button 
            type="submit"
            :disabled="isResetting"
            :loading="isResetting"
          >
            Reset Password
          </Button>
        </div>
      </CardFooter>
    </form>
  </Card>
</template>
